package fr.frux.core.common.push

/**
 * Envoi du jeton d'appareil vers l'API (#252 P2). Ce contrat existe parce que `core-common` ne
 * peut voir ni `:shared` ni `core-network` (CLAUDE.md règle 4) : le socle déclare ce dont il a
 * besoin, `:shared` le lui fournit. Même renversement que `FileExporter`, avec une dépendance
 * entrante en plus.
 *
 * Rend un booléen et ne lève jamais : l'appelant est un service système Android (`onNewToken`),
 * où une exception ne trouverait personne pour l'afficher.
 */
interface PushTokenSink {
    suspend fun register(
        token: String,
        platform: String,
    ): Boolean

    suspend fun revoke(token: String): Boolean
}

/**
 * Issue d'un [PushRegistrar.ensureRegistered]. [PermissionDenied] et [Unsupported] sont distincts
 * parce qu'ils appellent des réponses opposées côté écran : le premier se rattrape (l'utilisateur
 * peut accorder la permission), le second est définitif sur cette plateforme et ne doit produire
 * aucun message d'erreur — le toggle y est simplement sans effet.
 */
sealed interface PushRegistrationResult {
    data object Registered : PushRegistrationResult

    data object PermissionDenied : PushRegistrationResult

    data object Unsupported : PushRegistrationResult

    data class Failed(
        val message: String,
    ) : PushRegistrationResult
}

/**
 * Enregistrement de l'appareil auprès du service de push (#252 P2, ADR 0017).
 *
 * Android seul est implémenté au jalon M2 (ADR 0003) : le web n'a pas de Service Worker dans
 * l'app js Compose, et iOS attend le M4. Les autres plateformes rendent [Unsupported] sans lever,
 * exactement comme `FileExporter` sur desktop.
 */
interface PushRegistrar {
    /** `false` = cette plateforme (ou cette variante de build) ne sait pas recevoir de push. Le
     *  toggle de l'écran de préférences s'en sert pour se griser plutôt que d'échouer à l'usage. */
    val supported: Boolean

    /** État courant de la permission de notification, sans jamais la demander (#285) : sert aux
     *  réconciliations silencieuses (démarrage de session, ouverture des préférences), qui ne
     *  doivent pas faire surgir la boîte de dialogue système. `false` quand [supported] l'est
     *  aussi — la question n'a pas de sens sans push. */
    fun permissionGranted(): Boolean

    /** Permission système si nécessaire, puis récupération du jeton et enregistrement auprès de
     *  l'API. Idempotent : appelable à chaque activation du toggle et à chaque nouveau jeton. */
    suspend fun ensureRegistered(): PushRegistrationResult

    /** Révocation du jeton **côté API** (déconnexion). Le jeton local reste valide : à la
     *  reconnexion, un simple `ensureRegistered` suffit, sans nouvelle demande de permission. */
    suspend fun unregister()
}

expect fun createPushRegistrar(sink: PushTokenSink): PushRegistrar
